import React, { useState, useEffect, useContext } from 'react';
import {
	faDownload,
	faPlus,
	faChevronLeft,
	faChevronRight,
	faInbox,
	faTrash,
	faEdit,
	faEllipsisV
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Table, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import { format } from "date-fns";
import ReactPaginate from "react-paginate";
import { PostData, DownloadData } from '../../../services/FetchData';
import { ProductContext } from '../../../store';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import './AIAssistant.css';

const AIAssistantTrainingList = () => {
	const { currentWebsiteId } = useContext(ProductContext);
	const navigate = useNavigate();
	const [pageLoader, setPageLoader] = useState(true);
	const [trainings, setTrainings] = useState([]);
	const [totalTrainings, setTotalTrainings] = useState(0);
	const [currentPage, setCurrentPage] = useState(0);
	const [itemsPerPage] = useState(10);
	const [deleteModal, setDeleteModal] = useState(false);
	const [selectedTraining, setSelectedTraining] = useState(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [dropdownOpen, setDropdownOpen] = useState(null);
	const [isPythonRagTraining, setIsPythonRagTraining] = useState(false);

	const toggleDropdown = (trainingId) => {
		setDropdownOpen(dropdownOpen === trainingId ? null : trainingId);
	};

	// Fetch trainings from API
	useEffect(() => {
		const fetchTrainings = async () => {
			const websiteId = currentWebsiteId || Cookies.get("_website_id");

			if (!websiteId) {
				console.error("No website ID found");
				toast.error("Website ID is missing. Please refresh the page.");
				setPageLoader(false);
				return;
			}

			setPageLoader(true);
			try {
				const postData = {
					websiteId: websiteId,
					page: currentPage + 1,
					limit: itemsPerPage,
					searchTerm: appliedSearchTerm || ''
				};

				console.log("Fetching trainings with data:", postData);

				const response = await PostData("ms1", "api/v1/aiassistanttraining/list", postData);

				console.log("API Response:", response);

				if (response !== "Invalid" && response !== undefined) {
					if (response.status === "success") {
						const trainingsData = response.data.trainings || [];
						setTrainings(trainingsData);
						setTotalTrainings(response.data.total || 0);
						setIsPythonRagTraining(Boolean(response.data.isPythonRagTraining));
					} else if (response.status === "failure" || response.status === "not_found_error") {
						console.error("Failed response:", response);
						const errorMessage = response.message?.message || response.message || "Failed to fetch trainings";
						toast.error(errorMessage);
						setTrainings([]);
						setTotalTrainings(0);
					} else {
						console.error("Unexpected response format:", response);
						toast.error(response.message?.message || "Failed to fetch trainings");
						setTrainings([]);
						setTotalTrainings(0);
					}
				} else {
					console.error("Invalid response:", response);
					toast.error("Invalid response from server");
					setTrainings([]);
					setTotalTrainings(0);
				}
			} catch (error) {
				console.error("Error fetching trainings:", error);
				toast.error("An error occurred while fetching trainings");
				setTrainings([]);
				setTotalTrainings(0);
			} finally {
				setPageLoader(false);
			}
		};

		fetchTrainings();
	}, [currentPage, itemsPerPage, appliedSearchTerm, currentWebsiteId]);

	const handleAddTraining = () => {
		navigate('/aiassistant/training/add');
	};

	const handleEditClick = (training) => {
		navigate(`/aiassistant/training/edit/${training.trainingId}`);
	};

	const handleDeleteClick = (training) => {
		setSelectedTraining(training);
		setDeleteModal(true);
	};

	const confirmDelete = async () => {
		if (selectedTraining) {
			const websiteId = currentWebsiteId || Cookies.get("_website_id");
			
			try {
				const response = await PostData("ms1", "api/v1/aiassistanttraining/deleteAiAssistantTrainingQuestionAnswer", {
					trainingId: selectedTraining.trainingId,
					websiteId: websiteId
				});

				if (response !== "Invalid" && response !== undefined && response.status === "success") {
					// Remove from UI
					setTrainings(prev => prev.filter(t => t.trainingId !== selectedTraining.trainingId));
					setTotalTrainings(prev => prev - 1);
					toast.success('Training deleted successfully');
				} else {
					toast.error(response?.message?.message || response?.message || 'Failed to delete training');
				}
			} catch (error) {
				console.error("Error deleting training:", error);
				toast.error('Error deleting training. Please try again.');
			} finally {
				setDeleteModal(false);
				setSelectedTraining(null);
			}
		}
	};

	const handlePageClick = (data) => {
		setCurrentPage(data.selected);
	};

	const handleClearFilters = () => {
		setSearchTerm('');
		setAppliedSearchTerm('');
		setCurrentPage(0);
		setIsFilterOpen(false);
	};

	const handleApplyFilters = () => {
		const trimmedSearchTerm = searchTerm.trim();
		setAppliedSearchTerm(trimmedSearchTerm);
		setCurrentPage(0);
		setIsFilterOpen(false);
	};

	const handleExportCSV = async () => {
		const websiteId = currentWebsiteId || Cookies.get("_website_id");

		if (isPythonRagTraining) {
			if (!websiteId) {
				toast.error("Website ID is missing");
				return;
			}

			try {
				const csvContent = await DownloadData(
					"ms1",
					"api/v1/aiassistanttraining/export",
					{
						websiteId,
						searchTerm: appliedSearchTerm || "",
					}
				);

				if (typeof csvContent !== "string") {
					throw new Error("Unexpected response from export");
				}

				const filename = `product_knowledge_training_${format(new Date(), "yyyy-MM-dd_HH-mm-ss")}.csv`;
				const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
				const link = document.createElement("a");
				const url = URL.createObjectURL(blob);
				link.setAttribute("href", url);
				link.setAttribute("download", filename);
				link.style.visibility = "hidden";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);

				const lineCount = csvContent.split("\n").filter((l) => l.trim()).length;
				const rowCount = Math.max(0, lineCount - 1);
				toast.success(rowCount === 0 ? "No training data to export" : `Exported ${rowCount} training row(s)`);
			} catch (error) {
				console.error("Error exporting CSV:", error);
				toast.error(error.message || "Failed to export CSV file. Please try again.");
			}
			return;
		}

		const csvContent = [
			['SKU', 'Question', 'Answer', 'Type', 'Confidence', 'Created'],
			...trainings.map(training => [
				training.sku || '',
				training.question || '',
				(training.answer || '').replace(/\n/g, ' '),
				training.type || '',
				training.confidence || '',
				training.createdAt ? format(new Date(training.createdAt), "yyyy-MM-dd") : ''
			])
		].map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')).join('\n');

		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', 'ai_trainings.csv');
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const getTypeLabel = (type) => {
		const typeLabels = {
			'correction': 'Correction',
			'restriction': 'Restriction',
			'product_spec': 'Product Spec',
			'policy': 'Policy',
			'suggestion': 'Suggestion'
		};
		return typeLabels[type] || type;
	};

	const getConfidenceBadgeColor = (confidence) => {
		switch (confidence) {
			case 'high':
				return '#28a745';
			case 'medium':
				return '#ffc107';
			case 'low':
				return '#dc3545';
			default:
				return '#6c757d';
		}
	};

	return (
		<React.Fragment>
			<div className="ai-assistant-training-list">
				<div className="d-flex justify-content-between align-items-start mb-4">
					<div>
						<h2 className="mb-1">PDP Training</h2>
						<p className="text-muted mb-0">Manage PDP training data for product-specific answers</p>
					</div>
					<div className="d-flex gap-2">
						<Button
							className="filter-btn"
							color="outline-secondary"
							onClick={() => {
								if (!isFilterOpen) {
									setSearchTerm(appliedSearchTerm);
								}
								setIsFilterOpen(!isFilterOpen);
							}}
						>
							<i className="fa fa-filter"></i>
							Filter
						</Button>
						<Button
							color="outline-secondary"
							className="d-flex align-items-center export-button"
							onClick={handleExportCSV}
						>
							<FontAwesomeIcon icon={faDownload} className="me-2" />
							Export CSV
						</Button>
						<Button
							color="primary"
							className="d-flex align-items-center conversionBox"
							onClick={handleAddTraining}
						>
							<FontAwesomeIcon icon={faPlus} className="me-2" />
							Add PDP Training
						</Button>
					</div>
				</div>

				{pageLoader ? (
					<DetailSkeleton />
				) : (
					<React.Fragment>
						<div className="table-responsive">
							<Table className="data-table">
								<thead>
									<tr>
										<th>SKU</th>
										<th>Question</th>
										<th>Answer</th>
										<th>Type</th>
										<th>Confidence</th>
										<th>Action</th>
									</tr>
								</thead>
								<tbody>
									{trainings.length > 0 ? (
										trainings.map((training) => (
											<tr key={training.trainingId}>
												<td>
													<div className="searchItemStatusWrp">
														{training.sku}
													</div>
												</td>
												<td>
													<div className="searchItemStatusWrp">
														<div
															className="question-text"
															title={training.question}
														>
															{training.question}
														</div>
													</div>
												</td>
												<td>
													<div className="searchItemStatusWrp">
														<div
															className="answer-text"
															title={training.answer}
														>
															{training.answer}
														</div>
													</div>
												</td>
												<td>
													<div className="searchItemStatusWrp">
														<span className="badge" style={{ backgroundColor: '#005EFF', color: 'white', padding: '5px 8px', borderRadius: '5px', fontSize: '0.7rem' }}>
															{getTypeLabel(training.type)}
														</span>
													</div>
												</td>
												<td>
													<div className="searchItemStatusWrp">
														<span
															className="badge"
															style={{
																backgroundColor: getConfidenceBadgeColor(training.confidence),
																color: training.confidence === 'medium' ? '#333' : 'white',
																padding: '5px 8px',
																borderRadius: '5px',
																fontSize: '0.7rem',
																textTransform: 'capitalize'
															}}
														>
															{training.confidence}
														</span>
													</div>
												</td>
												<td>
													<Dropdown
														isOpen={dropdownOpen === training.trainingId}
														toggle={() => toggleDropdown(training.trainingId)}
													>
														<DropdownToggle
															tag="span"
															data-toggle="dropdown"
															aria-expanded={dropdownOpen === training.trainingId}
															style={{ cursor: 'pointer' }}
														>
															<FontAwesomeIcon icon={faEllipsisV} />
														</DropdownToggle>
														<DropdownMenu end>
															<DropdownItem onClick={() => handleEditClick(training)}>
																<span className="dropDownItemWrapper">
																	<FontAwesomeIcon icon={faEdit} /> Edit PDP Training
																</span>
															</DropdownItem>
															<DropdownItem onClick={() => handleDeleteClick(training)}>
																<span className="dropDownItemWrapper">
																	<FontAwesomeIcon icon={faTrash} /> Delete PDP Training
																</span>
															</DropdownItem>
														</DropdownMenu>
													</Dropdown>
												</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan={6} className="text-center p-5">
												<div className="d-flex flex-column align-items-center">
													<FontAwesomeIcon icon={faInbox} style={{ fontSize: '48px', color: '#dee2e6' }} />
													<p className="text-muted mt-3 mb-0">No PDP Training Data Found</p>
													<Button
														color="primary"
														className="mt-3 conversionBox"
														onClick={handleAddTraining}
													>
														<FontAwesomeIcon icon={faPlus} className="me-2" />
														Add Your First PDP Training
													</Button>
												</div>
											</td>
										</tr>
									)}
								</tbody>
							</Table>
						</div>
					</React.Fragment>
				)}

				{totalTrainings > itemsPerPage && (
					<div className="d-flex justify-content-center mt-4">
						<ReactPaginate
							previousLabel={<FontAwesomeIcon icon={faChevronLeft} />}
							nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
							pageCount={Math.ceil(totalTrainings / itemsPerPage)}
							onPageChange={handlePageClick}
							forcePage={currentPage}
							containerClassName="pagination"
							previousLinkClassName="page-link"
							nextLinkClassName="page-link"
							pageLinkClassName="page-link"
							activeClassName="active"
							disabledClassName="disabled"
						/>
					</div>
				)}

				{/* Filter Sidebar */}
				{isFilterOpen && (
					<div className="filter-sidebar-overlay" onClick={() => {
						setSearchTerm(appliedSearchTerm);
						setIsFilterOpen(false);
					}}>
						<div className="filter-sidebar" onClick={(e) => e.stopPropagation()}>
							<div className="filter-sidebar-header">
								<h5 className="filter-sidebar-title">Filter</h5>
								<Button
									className="close-filter-btn"
									color="link"
									onClick={() => {
										setSearchTerm(appliedSearchTerm);
										setIsFilterOpen(false);
									}}
								>
									<i className="fa fa-times"></i>
								</Button>
							</div>

							<div className="filter-sidebar-content">
								<div className="filter-section">
									<label className="filter-label">Search</label>
									<Input
										type="text"
										placeholder="Search by SKU, question, or answer..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="filter-input"
									/>
								</div>
							</div>

							<div className="filter-sidebar-footer">
								<Button
									className="clear-all-btn"
									color="secondary"
									outline
									onClick={handleClearFilters}
									style={{
										flex: 1,
										marginRight: '8px'
									}}
								>
									Clear All
								</Button>
								<Button
									className="apply-filters-btn"
									color="primary"
									onClick={handleApplyFilters}
									style={{
										flex: 1
									}}
								>
									Apply Filters
								</Button>
							</div>
						</div>
					</div>
				)}

				{/* Delete Confirmation Modal */}
				<Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} className="delete-confirmation-modal">
					<ModalHeader toggle={() => setDeleteModal(false)}>
						Delete PDP Training
					</ModalHeader>
					<ModalBody>
						Are you sure you want to delete this training data? This action cannot be undone.
					</ModalBody>
					<ModalFooter className="delete-modal-footer">
						<Button className="delete-cancel-button" onClick={() => setDeleteModal(false)}>
							Cancel
						</Button>
						<Button className="delete-confirm-button" onClick={confirmDelete}>
							Delete
						</Button>
					</ModalFooter>
				</Modal>
			</div>
		</React.Fragment>
	);
}

export default AIAssistantTrainingList;

